import { useNavigate, useSearchParams } from "@solidjs/router";
import { AiOutlineArrowLeft } from "solid-icons/ai";
import { FiFilter } from "solid-icons/fi";
import {
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
  Show,
  Suspense,
  untrack,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import { mapSemanticSearchError } from "@/api/utils/semantic-search-error";
import { ActiveSearchLayout } from "@/components/layout/ActiveSearchLayout";
import { Container } from "@/components/shared/Container";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsIndicator,
  TabsList,
  TabsTrigger,
  TabsTriggerDescription,
  TabsTriggerTitle,
} from "@/components/ui/tabs";
import { MetaTitleWithSeparator } from "@/domains/business/seo";
import { useInfiniteSearchFreelancers } from "@/domains/company/active-search/api";
import { FreelancerFiltersSheet } from "@/domains/company/active-search/components/FreelancerFiltersSheet";
import { FreelancerFiltersSidebar } from "@/domains/company/active-search/components/FreelancerFiltersSidebar";
import { FreelancerSelectionFloatingBar } from "@/domains/company/active-search/components/FreelancerSelectionFloatingBar";
import { FreelancerSelectionSummaryBar } from "@/domains/company/active-search/components/FreelancerSelectionSummaryBar";
import { FreelanceSheet } from "@/domains/company/active-search/components/freelance-sheet";
import { LeaveConfirmationDialog } from "@/domains/company/active-search/components/LeaveConfirmationDialog";
import { SearchResultsPanel } from "@/domains/company/active-search/components/SearchResultsPanel";
import { useFreelancerFiltersFromSearchParams } from "@/domains/company/active-search/hooks/useBuildFreelancerFilters";
import { useCvSelection } from "@/domains/company/active-search/hooks/useCvSelection";
import { useIsFixedOfferContext } from "@/domains/company/active-search/hooks/useIsFixedOfferContext";
import { useOfferTaxonomy } from "@/domains/company/active-search/hooks/useOfferTaxonomy";
import { useResultTabState } from "@/domains/company/active-search/hooks/useResultTabState";
import { useSearchAbandonTracking } from "@/domains/company/active-search/hooks/useSearchAbandonTracking";
import { useSearchCvDownload } from "@/domains/company/active-search/hooks/useSearchCvDownload";
import {
  type SearchActivity,
  useUserSearchInProgress,
} from "@/domains/company/active-search/hooks/useUserSearchInProgress";
import {
  useFreelanceSearchActions,
  useFreelanceSearchStore,
} from "@/domains/company/active-search/store/freelance-search-store";
import type {
  FreelancerSearchResult,
  FreelanceSheetData,
  SheetOrigin,
} from "@/domains/company/active-search/types";
import {
  BACK_TO_SEARCH_KEYS,
  buildPathWithQueryParams,
  filtersToFormDefaultValues,
  freelancerFilterFormToQueryParams,
} from "@/domains/company/active-search/utils/filterParams";
import {
  type ResultTab,
  shouldShowResultTabs,
} from "@/domains/company/active-search/utils/result-tabs";
import type { PreservedResults } from "@/domains/company/active-search/utils/results-display";
import { CvSelectionFloatingBar } from "@/domains/company/anonymization/CvSelectionFloatingBar";
import { isResultReachedViaRerun } from "@/domains/company/recent-searches/store/rerun-intent";
import { useTalentPoolCapabilities } from "@/domains/company/talent-pool/api";
import type { PoolMatchFreelancer } from "@/domains/company/talent-pool/api/types";
import { PoolMatchPanel } from "@/domains/company/talent-pool/components/match/pool-match-panel";
import { PremiumLockBadge } from "@/domains/company/talent-pool/components/premium-lock-badge";
import { UpsellModal } from "@/domains/company/talent-pool/components/upsell-modal";
import {
  TalentPoolProvider,
  useTalentPool,
} from "@/domains/company/talent-pool/context/talent-pool-provider";
import { poolMatchToSearchResult } from "@/domains/company/talent-pool/utils/match-card-presentation";
import { newIdsToHydrate } from "@/domains/company/talent-pool/utils/membership";
import { usePostHog } from "@/hooks/usePostHog";

/**
 * Renders the Talent Pool upsell modal bound to the provider's upsell state.
 * Must be a descendant of `TalentPoolProvider` so it can call `useTalentPool()`.
 * The modal fires its own `view_talent_pool_upsell` on the open edge — this host
 * only wires open/close, never double-fires.
 */
function TalentPoolUpsellHost() {
  const tp = useTalentPool();
  return (
    <UpsellModal
      open={tp.upsell.isOpen}
      onOpenChange={(open) => {
        if (!open) tp.upsell.close();
      }}
      source={tp.upsell.source ?? "card"}
    />
  );
}

/** Membership lookup accepts at most this many ids per call (BE cap, D4). */
const MEMBERSHIP_BATCH_SIZE = 200;

/**
 * Fills the Talent Pool membership map for the freelancers currently on screen,
 * one lookup per loaded page. Must be a descendant of `TalentPoolProvider` so it
 * can call `useTalentPool()`.
 *
 * `props.ids` is a reactive accessor to the flattened result-id list. A local
 * `requested` set tracks every id already dispatched, so the effect:
 *   - hydrates all ids on the first run (reload / first page);
 *   - hydrates only the newly appended ids on each load-more (`fetchNextPage`
 *     grows the list, the effect re-runs);
 *   - never re-requests on a reactive re-run with no new ids.
 *
 * Ids are dispatched in chunks of ≤`MEMBERSHIP_BATCH_SIZE` to stay under the BE
 * cap even if two pages land between runs, and marked requested only AFTER their
 * chunk is dispatched. Hydration is non-blocking and silent on failure (the
 * provider owns that): cards render immediately and hearts/pills fill when the
 * map arrives; the free plan gets an empty map and the page keeps working.
 */
function TalentPoolHydrator(props: { ids: () => string[] }) {
  const tp = useTalentPool();
  const requested = new Set<string>();

  createEffect(() => {
    const fresh = newIdsToHydrate(props.ids(), requested);
    if (fresh.length === 0) return;
    for (let i = 0; i < fresh.length; i += MEMBERSHIP_BATCH_SIZE) {
      const chunk = fresh.slice(i, i + MEMBERSHIP_BATCH_SIZE);
      tp.hydrate(chunk);
      for (const id of chunk) requested.add(id);
    }
  });

  return null;
}

/**
 * The "Talent pool" segmented-control trigger. A descendant of both `Tabs` and
 * `TalentPoolProvider` so it can show a lock affordance (AC8 anti-dead-end) once
 * capabilities resolve to a plan without the pool. Nothing is shown while
 * capabilities are still pending (D16 — no premature lock). The lock reuses the
 * freelancer-card `PremiumLockBadge` so the paywall reads as one system.
 */
function PoolTabTrigger() {
  const { t } = useI18n();
  const tp = useTalentPool();
  const locked = createMemo(
    () => tp.capabilities.status === "success" && !tp.capabilities.enabled,
  );

  return (
    <TabsTrigger value="pool" variant="descriptive">
      <TabsTriggerTitle>
        <Show when={locked()}>
          <PremiumLockBadge />
        </Show>
        {t("company.talent_pool.match.tabs.pool")}
      </TabsTriggerTitle>
      <TabsTriggerDescription>
        {t("company.talent_pool.match.tabs.pool_description")}
      </TabsTriggerDescription>
    </TabsTrigger>
  );
}

export default function CompanySearchFreelancerResultRoute() {
  const { t } = useI18n();
  const { capture } = usePostHog();
  // Same cached capabilities query the provider subscribes to (dedup by key) —
  // read here only to segment the tab-switch event by plan.
  const capabilities = useTalentPoolCapabilities();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedProfile, setSelectedProfile] =
    createSignal<FreelanceSheetData | null>(null);
  const [isSheetOpen, setIsSheetOpen] = createSignal(false);
  const [isFiltersSheetOpen, setIsFiltersSheetOpen] = createSignal(false);
  // Whether the open sheet must hide the contact action (non-discoverable pool
  // card, D9). Search cards never disable it; the pool panel (T9) flips it.
  const [sheetDisableContact, setSheetDisableContact] = createSignal(false);
  // Da quale tab è partita l'apertura: endpoint del profilo + funnel (AC23).
  const [sheetSource, setSheetSource] = createSignal<SheetOrigin>("search");

  const filters = useFreelancerFiltersFromSearchParams(searchParams);
  const [debouncedFilters, setDebouncedFilters] = createSignal(filters());

  createEffect(() => {
    const nextFilters = filters();
    const timeout = window.setTimeout(
      () => setDebouncedFilters(nextFilters),
      400,
    );
    onCleanup(() => window.clearTimeout(timeout));
  });

  const [storeState] = useFreelanceSearchStore();
  const actions = useFreelanceSearchActions();
  const isFixedOffer = useIsFixedOfferContext();
  const offerTaxonomy = useOfferTaxonomy();

  // Tabs appear ⟺ the search is offer-linked (from the persisted store, not the
  // URL). Independent of plan (AC1) — the plan only decides what the pool tab
  // renders. `useResultTabState` seeds `?tab=ricerca` on entry (AC2).
  const showTabs = createMemo(() =>
    shouldShowResultTabs(storeState.researchType, storeState.jobOfferId),
  );
  const tabState = useResultTabState(showTabs);

  // The subcategories hint under the field only holds on the «Talent pool» tab,
  // where the offer's subcategories still count. An accessor: the sidebar does
  // not remount on a tab change.
  const offerContext = () => ({
    active: showTabs() && tabState.activeTab() === "pool",
    offer: offerTaxonomy(),
  });

  // Selezione CV anonimi: la stessa che alimenta la CTA del dettaglio (store
  // persistito), letta qui per la barra flottante.
  const cvSelection = useCvSelection();
  /* A download riuscito la selezione si svuota (e con essa sparisce la barra):
     i CV di quei candidati sono ormai sul disco dell'utente, tenerli in canna
     costringerebbe a sparecchiare a mano prima del gruppo successivo. Su errore
     la selezione resta, così riprovare non impone di ricomporla. */
  const cvDownload = useSearchCvDownload(() => cvSelection.clear());

  const [globalSearchFilters, setGlobalSearchFilters] = createSignal(
    debouncedFilters(),
  );

  // Keep the global semantic query mounted so a tab round-trip cannot retry a
  // failed 429. While the pool tab is visible its key is frozen, preventing a
  // second paid request when shared filters change; switching back adopts the
  // latest debounced filters as an explicit user navigation.
  createEffect(() => {
    if (!showTabs() || tabState.activeTab() === "ricerca") {
      setGlobalSearchFilters(debouncedFilters());
    }
  });

  // Tab-switch tracking (FLOW Goal — pool switch-rate). Kobalte fires onChange
  // only on user selection (the controlled `value` doesn't change on the mount
  // seed ricerca→ricerca), so this captures genuine switches, not the initial
  // render. "switch" is not an allowed verb → `click_*`.
  const handleTabChange = (value: string) => {
    const tab = value as ResultTab;
    capture("click_search_result_tab", {
      tab,
      job_offer_id: storeState.jobOfferId ?? null,
      plan: capabilities.data?.plan ?? null,
    });
    tabState.setTab(tab);
  };

  createEffect(() => {
    if (!isFixedOffer()) return;
    if (
      searchParams.price_per_day_min === undefined &&
      searchParams.price_per_day_max === undefined
    ) {
      return;
    }
    setSearchParams(
      { price_per_day_min: undefined, price_per_day_max: undefined },
      { replace: true },
    );
  });

  createEffect(() => {
    const f = filters();
    const researchType = storeState.researchType;
    const jobOfferId = storeState.jobOfferId;
    if (f && (researchType != null || jobOfferId != null)) {
      const cleaned = isFixedOffer()
        ? (() => {
            const {
              price_per_day_min: _min,
              price_per_day_max: _max,
              ...rest
            } = f;
            return rest;
          })()
        : f;
      actions.seedInitialResultFilters(cleaned, { researchType, jobOfferId });
      actions.setProposalPreFillFilters(cleaned);
    }
  });

  const searchQuery = useInfiniteSearchFreelancers(globalSearchFilters);

  /* La route possiede l'istante di partenza della prima ricerca (AC2/E-3): il
     panel si smonta al giro di tab, la route no, quindi la narrazione riparte
     dal punto giusto. Fronte di salita di "fetch di prima ricerca in volo"
     (fetch in volo e nessun dato, nemmeno placeholder): copre anche il retry
     post-errore, che resta `status: "error"` durante il volo (D12) e deve
     ri-ancorare la narrazione. */
  const [searchStartedAt, setSearchStartedAt] = createSignal(Date.now());
  /* ⚠ `isPending` prima di `data`: solid-query sospende sull'accesso a
     `data` in pending iniziale, e questa route vive dentro la Suspense del
     router — una lettura non guardata mostrerebbe lo skeleton di app al
     posto della loading screen.
     "in volo" è `fetchStatus !== "idle"`: con `networkMode: "online"` una
     pausa di rete lascia la richiesta pendente, non la conclude. Trattarla
     come uscita ri-ancorerebbe `searchStartedAt` alla ripresa e falserebbe
     l'`elapsed_ms` di abbandono (AC22) della durata della pausa. */
  const isFirstSearchInFlight = createMemo(
    () =>
      searchQuery.fetchStatus !== "idle" &&
      (searchQuery.isPending || searchQuery.data === undefined),
  );
  createEffect(
    on(isFirstSearchInFlight, (inFlight, prev) => {
      if (inFlight && !prev) setSearchStartedAt(Date.now());
    }),
  );

  /* AC22 — funnel della ricerca semantica sui fronti del volo (accumulatore
     esplicito: al primo run il fetch può essere già in volo). "Carica altri"
     resta fuori dal funnel (ha il suo stato, AC7/AC12).
     Il fronte è "in volo" (`fetchStatus !== "idle"`), non "fetching": `paused`
     è una pausa di rete, non un'uscita. Chiuderci sopra il volo emetterebbe un
     `start_` orfano — niente `end_`, niente `fail_` — e alla ripresa un
     secondo `start_` con un `Date.now()` nuovo, sottostimando `duration_ms`
     esattamente della durata della pausa. */
  let searchFlight: { startedAt: number; type: "first" | "refine" } | null =
    null;
  createEffect<boolean | undefined>((prevInFlight) => {
    const inFlight = searchQuery.fetchStatus !== "idle";
    if (inFlight && !prevInFlight) {
      if (!untrack(() => searchQuery.isFetchingNextPage)) {
        const type = untrack(isFirstSearchInFlight) ? "first" : "refine";
        searchFlight = { startedAt: Date.now(), type };
        untrack(() =>
          capture("start_semantic_search", {
            search_type: type,
            job_offer_id: storeState.jobOfferId ?? null,
          }),
        );
      }
    }
    if (!inFlight && prevInFlight && searchFlight) {
      const flight = searchFlight;
      searchFlight = null;
      untrack(() => {
        if (searchQuery.status === "success") {
          capture("end_semantic_search", {
            duration_ms: Date.now() - flight.startedAt,
            result_count: searchQuery.data?.pages?.[0]?.pagination?.total ?? 0,
            search_type: flight.type,
          });
        } else if (searchQuery.error) {
          capture("fail_semantic_search", {
            endpoint: "freelancers_search",
            error_kind: mapSemanticSearchError(searchQuery.error).kind,
            duration_ms: Date.now() - flight.startedAt,
            search_type: flight.type,
          });
        }
      });
    }
    return inFlight;
  }, undefined);

  const freelancers = createMemo(() =>
    searchQuery.isPending
      ? []
      : (searchQuery.data?.pages.flatMap((p) => p.data) ?? []),
  );

  /* AC11/F-4: fotografia dell'ultima lista buona. `keepPreviousData` molla i
     dati sull'errore, e il Back non recupera (URL in `replace`): la route
     conserva lo snapshot a ogni successo e il panel lo rende dietro
     l'etichetta "ricerca precedente" quando la query fallisce.
     Ogni successo SOVRASCRIVE, zero risultati compresi: un successo vuoto
     azzera lo snapshot, perché "la ricerca precedente non ha trovato nulla"
     non è "non c'era una ricerca precedente". Saltare la generazione a zero
     farebbe risorgere la lista di due ricerche prima sotto un'etichetta che
     afferma il falso; con lo snapshot azzerato l'errore successivo cade
     correttamente su "errore senza nulla da conservare". */
  const [lastGoodResults, setLastGoodResults] =
    createSignal<PreservedResults | null>(null);
  createEffect(() => {
    if (searchQuery.status !== "success") return;
    const flattened = freelancers();
    if (flattened.length === 0) {
      setLastGoodResults(null);
      return;
    }
    setLastGoodResults({
      freelancers: flattened,
      total:
        searchQuery.data?.pages?.[0]?.pagination?.total ?? flattened.length,
    });
  });

  /* AC22 — abbandono durante la loading screen: il numero che deve calare. */
  useSearchAbandonTracking({
    isLoadingVisible: () => isFirstSearchInFlight() && !lastGoodResults(),
    startedAt: searchStartedAt,
    capture,
  });

  const filterFormDefaults = createMemo(() =>
    filtersToFormDefaultValues(filters()),
  );
  const filtersKey = createMemo(() => JSON.stringify(filters()));
  /* Chiave su cui la query è davvero montata (`useInfiniteSearchFreelancers`
     la usa come queryKey): finché `filtersKey()` la precede, il debounce è
     ancora in corso e un fetch arriverà per costruzione. */
  const appliedFiltersKey = createMemo(() =>
    JSON.stringify(globalSearchFilters()),
  );

  /* AC8/D8: il segnale di ricerca-in-corso parte dalla CONFERMA dell'utente
     (Applica/Reset/retry con lista conservata), pre-debounce; si spegne
     all'assestamento del fetch NATO dopo quella conferma, o da solo se la
     conferma non produce nulla (Applica identico). Mai sul tab pool (AC12/D6).

     Il canale maschera la paginazione a "idle": `fetchNextPage` alza lo stesso
     `fetchStatus` della ricerca, e senza la maschera una conferma durante
     "Carica altri" adotterebbe il fetch di paginazione — segnale acceso
     durante la paginazione (che AC12 vieta) e spento all'arrivo della pagina
     2, cioè invertito. Le due chiavi sono il fatto osservabile che tiene
     aperta la finestra della conferma senza affidarla a una durata. */
  const searchActivity = createMemo<SearchActivity>(() => ({
    fetchStatus: searchQuery.isFetchingNextPage
      ? "idle"
      : searchQuery.fetchStatus,
    pendingKey: filtersKey(),
    appliedKey: appliedFiltersKey(),
  }));
  const userSearch = useUserSearchInProgress(searchActivity);
  const confirmUserSearch = () => {
    if (showTabs() && tabState.activeTab() !== "ricerca") return;
    userSearch.confirm();
  };

  const handleApplyFilters = (
    values: Parameters<typeof freelancerFilterFormToQueryParams>[0],
  ) => {
    confirmUserSearch();
    setSearchParams(freelancerFilterFormToQueryParams(values), {
      replace: true,
    });
  };

  const handleResetFilters = () => {
    confirmUserSearch();
    const initial = storeState.initialResultFilters;
    if (initial) {
      setSearchParams(
        freelancerFilterFormToQueryParams(filtersToFormDefaultValues(initial)),
        {
          replace: true,
        },
      );
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  const handleViewProfile = (profile: FreelancerSearchResult) => {
    setSheetDisableContact(false);
    setSheetSource("search");
    setSelectedProfile(profile);
    setIsSheetOpen(true);
  };

  // Pool card → sheet: feed the sheet via the lean adapter (D7) and disable
  // contact for a non-discoverable row (D9/OQ4).
  const handleViewPoolProfile = (
    item: PoolMatchFreelancer,
    opts: { disableContact: boolean },
  ) => {
    setSheetDisableContact(opts.disableContact);
    setSheetSource("pool");
    setSelectedProfile(poolMatchToSearchResult(item));
    setIsSheetOpen(true);
  };

  const navigate = useNavigate();

  const navigateToSearch = () => {
    // A rerun from «Le tue ricerche recenti» never visited `/offers` or
    // `/search` — reconstructing one of them from the current filters would
    // strand the user on a page unrelated to where they came from. Fall back
    // to a real history pop, which lands back on the entry page instead.
    if (isResultReachedViaRerun()) {
      navigate(-1);
      return;
    }

    const f = filters();
    const paramsObj = freelancerFilterFormToQueryParams(
      filtersToFormDefaultValues(f),
    );
    const researchType = storeState.researchType;

    const destinationPath =
      researchType === "from_offer"
        ? "/profile/company/search/freelancers/offers"
        : "/profile/company/search/freelancers/search";
    navigate(
      buildPathWithQueryParams(destinationPath, paramsObj, {
        includeKeys: BACK_TO_SEARCH_KEYS,
      }),
    );
  };

  return (
    <TalentPoolProvider>
      <Show when={storeState.selectedFreelancers.length > 0}>
        <FreelancerSelectionFloatingBar />
      </Show>

      {/* Gemella della barra "Contatta", su una selezione separata. Entrambe
          sono `fixed top-4` centrate: quando convivono, questa scende sotto
          l'altra invece di sovrapporsi. */}
      <Show when={(storeState.selectedForCv ?? []).length > 0}>
        <CvSelectionFloatingBar
          selection={cvSelection}
          download={cvDownload}
          stacked={storeState.selectedFreelancers.length > 0}
        />
      </Show>

      <ActiveSearchLayout
        sidebar={
          <Show when={filtersKey()} keyed>
            {(_key) => (
              <FreelancerFiltersSidebar
                defaultValues={filterFormDefaults()}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                disablePriceFilter={isFixedOffer()}
                offerContext={offerContext}
              />
            )}
          </Show>
        }
      >
        <MetaTitleWithSeparator>
          {t("company.search.freelancers.result.title")}
        </MetaTitleWithSeparator>
        {/* `px-0`: `ActiveSearchLayout` already wraps children in a `Container`,
            so the mobile `px-4` was applied twice — 64px of padding on a 375px
            screen, enough on its own to overflow the viewport. `lg:px-2` is left
            doubled so desktop spacing is unchanged. */}
        <Container size="full" class="py-6 px-0">
          <div class="space-y-6">
            {/* Persistent chrome (outside the tab panels, D11): back button,
                  title, mobile filter trigger, selection bar — shared across
                  both tabs and identical on the free-search branch. */}
            <Button
              variant="ghost"
              size="sm"
              class="mb-4 p-0 h-auto hover:bg-transparent"
              onClick={navigateToSearch}
            >
              <AiOutlineArrowLeft size={20} class="mr-2" />
              {t("common.back")}
            </Button>

            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <h1 class="text-2xl font-bold text-gray-900">
                {t("company.search.freelancers.result.title")}
              </h1>
              <Button
                type="button"
                variant="outline"
                size="sm"
                class="lg:hidden shrink-0"
                onClick={() => setIsFiltersSheetOpen(true)}
              >
                <FiFilter class="mr-2 size-4" />
                {t("company.search.freelancers.result.filters.button")}
              </Button>
            </div>

            <Show when={storeState.selectedFreelancers.length > 0}>
              <FreelancerSelectionSummaryBar />
            </Show>

            <Show
              when={showTabs()}
              fallback={
                <SearchResultsPanel
                  search={searchQuery}
                  startedAt={searchStartedAt()}
                  lastGood={lastGoodResults()}
                  busy={userSearch.isInProgress()}
                  hasSelection={storeState.selectedFreelancers.length > 0}
                  onPreservedRetry={confirmUserSearch}
                  onResetFilters={handleResetFilters}
                  onViewProfile={handleViewProfile}
                  onBackToSearch={navigateToSearch}
                />
              }
            >
              <Tabs value={tabState.activeTab()} onChange={handleTabChange}>
                <TabsList>
                  <TabsTrigger value="ricerca" variant="descriptive">
                    <TabsTriggerTitle>
                      {t("company.talent_pool.match.tabs.search")}
                    </TabsTriggerTitle>
                    <TabsTriggerDescription>
                      {t("company.talent_pool.match.tabs.search_description")}
                    </TabsTriggerDescription>
                  </TabsTrigger>
                  <PoolTabTrigger />
                  <TabsIndicator />
                </TabsList>
                <TabsContent value="ricerca">
                  <SearchResultsPanel
                    search={searchQuery}
                    startedAt={searchStartedAt()}
                    lastGood={lastGoodResults()}
                    busy={userSearch.isInProgress()}
                    hasSelection={storeState.selectedFreelancers.length > 0}
                    onPreservedRetry={confirmUserSearch}
                    onResetFilters={handleResetFilters}
                    onViewProfile={handleViewProfile}
                    onBackToSearch={navigateToSearch}
                  />
                </TabsContent>
                <TabsContent value="pool">
                  <PoolMatchPanel
                    jobOfferId={storeState.jobOfferId ?? ""}
                    filters={debouncedFilters}
                    onViewProfile={handleViewPoolProfile}
                    onBackToSearch={navigateToSearch}
                    onResetFilters={handleResetFilters}
                    onGoToSearchTab={() => tabState.setTab("ricerca")}
                  />
                </TabsContent>
              </Tabs>
            </Show>
          </div>
        </Container>
      </ActiveSearchLayout>

      {/* Mobile: filters sheet */}
      <FreelancerFiltersSheet
        open={isFiltersSheetOpen()}
        onOpenChange={setIsFiltersSheetOpen}
        defaultValues={filterFormDefaults()}
        filtersKey={filtersKey()}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        disablePriceFilter={isFixedOffer()}
        offerContext={offerContext}
      />

      {/* Profile Sheet — the selection survives the close on purpose: clearing it
          here would unmount the sheet in the same tick `open` flips, and Kobalte
          would have nothing left to play the exit animation on. The sheet follows
          the id reactively instead, so switching freelancer still refetches.

          Its own boundary: the sheet mounts on the first selection and reads the
          profile query while it renders, from cache when the freelancer was
          opened before. A query hook used for the first time suspends the
          nearest `<Suspense>` on that read, even from cache, and again on each
          later refetch. Without this boundary that is the route's, which swaps
          the whole page out under the sheet: the card that opened it loses
          focus, and closing cannot give focus back. */}
      <Suspense>
        <Show when={selectedProfile()}>
          {(profile) => (
            <FreelanceSheet
              freelancer={profile()}
              open={isSheetOpen}
              onOpenChange={setIsSheetOpen}
              disableContact={sheetDisableContact()}
              origin={sheetSource()}
            />
          )}
        </Show>
      </Suspense>

      <LeaveConfirmationDialog
        when={() => storeState.selectedFreelancers.length > 0}
        onConfirm={() => actions.clearFreelancers()}
        excludedRoutes={["send-proposal", "new-proposal"]}
      />

      <TalentPoolUpsellHost />
      <TalentPoolHydrator ids={() => freelancers().map((f) => f.id)} />
    </TalentPoolProvider>
  );
}
